/**
 * SiteAssessmentPage.tsx — Draft Influent Data Entry
 *
 * Entry point for early-stage / conceptual influent data.
 * Supports two modes:
 *  • Indicative Data   — preliminary / scoping-level data
 *  • Draft Assessment  — estimated data for conceptual process design
 *
 * Saved samples are stored as influent type, linked to the selected
 * site and customer, and available for DAF / process train assessment.
 * The full regulatory Site Assessment workflow is at /parameters.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  MapPin, FlaskConical, Save, AlertTriangle, CheckCircle2,
  Building2, Calendar, Shield, Hash, FileText, ArrowRight,
  Database, RefreshCw,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useSession } from '@/context/SessionContext';
import {
  saveLocalInfluentSample,
  getLocalSites,
  type UnifiedSample,
} from '@/lib/hybridStorage';
import { buildRegulatoryReportNumber } from '@/lib/pdfGenerator';
import { ALL_STANDARDS } from '@/types/standards';

// ─── Types ────────────────────────────────────────────────────────────────────

type Purpose = 'site-visit' | 'draft-assessment';

interface SiteAssessmentForm {
  sampleName: string;
  siteId: string;
  date: string;
  notes: string;
  // Core
  ph: string; cod: string; bod5: string; tss: string;
  nh3n: string; tp: string; tn: string; oilgrease: string; tds: string;
  // Physical
  conductivity: string; temperature: string; turbidity: string; flow: string;
  // Metals
  lead: string; chromium: string; cadmium: string; nickel: string;
  zinc: string; copper: string; iron: string; manganese: string; arsenic: string;
  mercury: string;
  // Other
  no3n: string; sulfide: string; chloride: string; hardness: string;
  alkalinity: string; mlss: string; srt: string; do: string;
  phenol: string; surfactants: string;
}

const EMPTY_FORM: SiteAssessmentForm = {
  sampleName: '',
  siteId: '',
  date: new Date().toISOString().split('T')[0],
  notes: '',
  ph: '', cod: '', bod5: '', tss: '', nh3n: '', tp: '', tn: '',
  oilgrease: '', tds: '',
  conductivity: '', temperature: '', turbidity: '', flow: '',
  lead: '', chromium: '', cadmium: '', nickel: '', zinc: '', copper: '',
  iron: '', manganese: '', arsenic: '', mercury: '',
  no3n: '', sulfide: '', chloride: '', hardness: '',
  alkalinity: '', mlss: '', srt: '', do: '',
  phenol: '', surfactants: '',
};

// ─── Field config ─────────────────────────────────────────────────────────────

interface FieldMeta { label: string; unit: string; }

const CORE_FIELDS: { key: keyof SiteAssessmentForm; meta: FieldMeta }[] = [
  { key: 'ph',          meta: { label: 'pH',            unit: '' } },
  { key: 'cod',         meta: { label: 'COD',           unit: 'mg/L' } },
  { key: 'bod5',        meta: { label: 'BOD₅',          unit: 'mg/L' } },
  { key: 'tss',         meta: { label: 'TSS',           unit: 'mg/L' } },
  { key: 'nh3n',        meta: { label: 'NH₃-N',         unit: 'mg/L' } },
  { key: 'tp',          meta: { label: 'Total P',       unit: 'mg/L' } },
  { key: 'tn',          meta: { label: 'Total N',       unit: 'mg/L' } },
  { key: 'oilgrease',   meta: { label: 'Oil & Grease',  unit: 'mg/L' } },
  { key: 'tds',         meta: { label: 'TDS',           unit: 'mg/L' } },
  { key: 'conductivity',meta: { label: 'Conductivity',  unit: 'µS/cm' } },
  { key: 'temperature', meta: { label: 'Temperature',   unit: '°C' } },
  { key: 'turbidity',   meta: { label: 'Turbidity',     unit: 'NTU' } },
  { key: 'flow',        meta: { label: 'Flow Rate',     unit: 'm³/day' } },
];

const ADDITIONAL_FIELDS: { key: keyof SiteAssessmentForm; meta: FieldMeta }[] = [
  { key: 'lead',        meta: { label: 'Lead (Pb)',     unit: 'mg/L' } },
  { key: 'chromium',    meta: { label: 'Chromium (Cr)', unit: 'mg/L' } },
  { key: 'cadmium',     meta: { label: 'Cadmium (Cd)',  unit: 'mg/L' } },
  { key: 'nickel',      meta: { label: 'Nickel (Ni)',   unit: 'mg/L' } },
  { key: 'zinc',        meta: { label: 'Zinc (Zn)',     unit: 'mg/L' } },
  { key: 'copper',      meta: { label: 'Copper (Cu)',   unit: 'mg/L' } },
  { key: 'iron',        meta: { label: 'Iron (Fe)',     unit: 'mg/L' } },
  { key: 'manganese',   meta: { label: 'Manganese',     unit: 'mg/L' } },
  { key: 'arsenic',     meta: { label: 'Arsenic (As)',  unit: 'mg/L' } },
  { key: 'mercury',     meta: { label: 'Mercury (Hg)', unit: 'mg/L' } },
  { key: 'no3n',        meta: { label: 'NO₃-N',         unit: 'mg/L' } },
  { key: 'sulfide',     meta: { label: 'Sulfides',      unit: 'mg/L' } },
  { key: 'chloride',    meta: { label: 'Chlorides',     unit: 'mg/L' } },
  { key: 'hardness',    meta: { label: 'Hardness',      unit: 'mg/L' } },
  { key: 'alkalinity',  meta: { label: 'Alkalinity',    unit: 'mg/L' } },
  { key: 'mlss',        meta: { label: 'MLSS',          unit: 'mg/L' } },
  { key: 'srt',         meta: { label: 'SRT',           unit: 'days' } },
  { key: 'do',          meta: { label: 'Dissolved O₂',  unit: 'mg/L' } },
  { key: 'phenol',      meta: { label: 'Phenol',        unit: 'mg/L' } },
  { key: 'surfactants', meta: { label: 'Surfactants',   unit: 'mg/L' } },
];

// ─── Field input ──────────────────────────────────────────────────────────────

function FieldInput({
  fieldKey, label, unit, value, onChange,
}: {
  fieldKey: string;
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const hasValue = value.trim() !== '';
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-bold uppercase tracking-wider flex items-center justify-between gap-1">
        <span className="text-slate-400">{label}</span>
        {unit && <span className="text-[10px] text-slate-400 font-normal normal-case">{unit}</span>}
      </label>
      <input
        id={fieldKey}
        type="number"
        step="any"
        min="0"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="—"
        className={`w-full px-3 py-2 rounded-lg bg-slate-800/50 border text-sm text-slate-200 placeholder-slate-700 focus:outline-none transition-all font-mono ${
          hasValue
            ? 'border-emerald-500/30 focus:border-emerald-500/50'
            : 'border-slate-800/50 focus:border-rb-brand/40'
        }`}
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SiteAssessmentPage() {
  const navigate = useNavigate();
  const { currentCustomer, activeStandard } = useSession();
  const { user } = useAuth();

  const [purpose, setPurpose] = useState<Purpose>('site-visit');
  const [form, setForm] = useState<SiteAssessmentForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [savedRef, setSavedRef] = useState<string | null>(null);

  const stdCode = (currentCustomer as any)?.standard || activeStandard || 'UK_EA';
  const std = ALL_STANDARDS.find(s => s.code === stdCode) || null;

  // Sites for current customer
  const customerSites = useMemo(() => {
    if (!currentCustomer) return [];
    const allSites = getLocalSites();
    const custId = String((currentCustomer as any).id || '');
    return allSites.filter(s => String(s.customerId) === custId);
  }, [currentCustomer]);

  const update = (key: keyof SiteAssessmentForm, val: string) => {
    setForm(prev => ({ ...prev, [key]: val }));
  };

  const buildParams = (): Record<string, number> => {
    const out: Record<string, number> = {};
    const numericKeys: Array<keyof SiteAssessmentForm> = [
      'ph', 'cod', 'bod5', 'tss', 'nh3n', 'tp', 'tn', 'oilgrease', 'tds',
      'conductivity', 'temperature', 'turbidity', 'flow',
      'lead', 'chromium', 'cadmium', 'nickel', 'zinc', 'copper',
      'iron', 'manganese', 'arsenic', 'mercury',
      'no3n', 'sulfide', 'chloride', 'hardness', 'alkalinity',
      'mlss', 'srt', 'do', 'phenol', 'surfactants',
    ];
    numericKeys.forEach(k => {
      const v = parseFloat(form[k] as string);
      if (!isNaN(v)) out[k as string] = v;
    });
    return out;
  };

  const handleSave = async () => {
    if (!currentCustomer) {
      toast.error('Select a customer first');
      return;
    }
    if (!form.sampleName.trim()) {
      toast.error('Enter a sample name');
      return;
    }

    setSaving(true);
    try {
      const refNum = buildRegulatoryReportNumber('ASM', stdCode);
      const purposeLabel = purpose === 'site-visit' ? 'INDICATIVE DATA' : 'DRAFT ASSESSMENT';
      const notesPrefix = `[DRAFT INFLUENT ENTRY — ${purposeLabel}]`;
      const notes = form.notes.trim()
        ? `${notesPrefix} ${form.notes.trim()}`
        : notesPrefix;

      const custId = Number((currentCustomer as any).id || 0);

      const sample: Partial<UnifiedSample> = {
        sampleName: form.sampleName.trim(),
        customerId: custId || null,
        siteId: form.siteId || null,
        standard: stdCode,
        params: buildParams(),
        notes,
        sampleType: 'influent',
        reportNumber: refNum,
        createdAt: new Date(form.date).toISOString(),
        userId: Number((user as any)?.id || 0),
      };

      saveLocalInfluentSample(sample);
      setSavedRef(refNum);
      toast.success(`Sample saved — ${refNum}`);
    } catch (err: any) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setSavedRef(null);
  };

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      {/* ── Page header ── */}
      <PageHeader icon={MapPin} title="Site Assessment" subtitle="On-site influent characterisation and draft data entry" />

      {/* ── Customer guard ── */}
      {!currentCustomer && (
        <div className="rb-card p-6 border border-amber-500/30 bg-amber-500/5 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-400">Select a customer first</p>
            <p className="text-xs text-slate-500 mt-0.5">Go to the Customers page to select an active customer before recording site data.</p>
          </div>
        </div>
      )}

      {currentCustomer && (
        <>
          {/* ── Purpose selector ── */}
          <div className="rb-card p-4">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-3">Data Type</p>
            <div className="flex gap-3">
              <button
                onClick={() => setPurpose('site-visit')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold transition-all ${
                  purpose === 'site-visit'
                    ? 'bg-rb-active border-rb-brand/40 text-rb-brand'
                    : 'bg-slate-800/40 border-slate-800/40 text-slate-400 hover:border-slate-600/60'
                }`}
              >
                <MapPin className="w-4 h-4" />
                Indicative Data
              </button>
              <button
                onClick={() => setPurpose('draft-assessment')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold transition-all ${
                  purpose === 'draft-assessment'
                    ? 'bg-rb-active border-rb-brand/40 text-rb-brand'
                    : 'bg-slate-800/40 border-slate-800/40 text-slate-400 hover:border-slate-600/60'
                }`}
              >
                <FlaskConical className="w-4 h-4" />
                Draft Assessment
              </button>
            </div>
            <p className="text-[10px] text-slate-600 mt-2 text-center">
              {purpose === 'site-visit'
                ? 'Indicative / preliminary data for early-stage process scoping'
                : 'Estimated or assumed data for conceptual process design'}
            </p>
          </div>

          {/* ── Metadata row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Sample name */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sample Name *</label>
              <input
                type="text"
                value={form.sampleName}
                onChange={e => update('sampleName', e.target.value)}
                placeholder="e.g. Site Visit Apr 2026"
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40 transition-all"
              />
            </div>

            {/* Date */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Sample Date
              </label>
              <input
                type="date"
                value={form.date}
                onChange={e => update('date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 focus:outline-none focus:border-rb-brand/40 transition-all"
              />
            </div>

            {/* Site selector */}
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Site
              </label>
              {customerSites.length === 0 ? (
                <div className="px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-800/30 text-xs text-slate-600">
                  No sites — add one in the Customers page
                </div>
              ) : (
                <select
                  value={form.siteId}
                  onChange={e => update('siteId', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-200 focus:outline-none focus:border-rb-brand/40 transition-all"
                >
                  <option value="">— No site selected —</option>
                  {customerSites.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* ── Standard badge ── */}
          {std && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rb-active/40 border border-rb-brand/15">
              <Shield className="w-3.5 h-3.5 text-rb-brand" />
              <span className="text-xs font-bold text-rb-brand">{std.flag} {std.shortName}</span>
              <span className="text-[10px] text-slate-500">— {std.name}</span>
              <span className="ml-auto text-[10px] text-slate-600">Active discharge standard</span>
            </div>
          )}

          {/* ── Parameter entry — two columns ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: Core parameters */}
            <div className="rb-card p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Database className="w-3.5 h-3.5 text-rb-brand" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wider">Core Parameters</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {CORE_FIELDS.map(({ key, meta }) => (
                  <FieldInput
                    key={key}
                    fieldKey={key}
                    label={meta.label}
                    unit={meta.unit}
                    value={form[key] as string}
                    onChange={v => update(key, v)}
                  />
                ))}
              </div>
            </div>

            {/* Right: Additional parameters */}
            <div className="rb-card p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <FlaskConical className="w-3.5 h-3.5 text-slate-500" />
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Additional Parameters</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {ADDITIONAL_FIELDS.map(({ key, meta }) => (
                  <FieldInput
                    key={key}
                    fieldKey={key}
                    label={meta.label}
                    unit={meta.unit}
                    value={form[key] as string}
                    onChange={v => update(key, v)}
                  />
                ))}
              </div>

              {/* Notes */}
              <div className="space-y-1 pt-1">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => update('notes', e.target.value)}
                  placeholder="Optional notes about this sample or site visit…"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-800/50 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:border-rb-brand/40 transition-all resize-none"
                />
              </div>
            </div>
          </div>

          {/* ── Save button ── */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !form.sampleName.trim()}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-rb-brand text-slate-200 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Sample'}
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-slate-400 hover:text-slate-200 border border-slate-800/50 hover:border-slate-600 transition-all"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          {/* ── Post-save panel ── */}
          {savedRef && (
            <div className="rb-card p-5 border border-emerald-500/20 bg-emerald-500/5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-emerald-400">Sample saved successfully</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Hash className="w-3 h-3 text-rb-brand" />
                    <span className="text-xs text-slate-400">Reference ID:</span>
                    <span className="text-xs font-bold font-mono text-rb-brand">{savedRef}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <FileText className="w-3 h-3 text-slate-500" />
                    <span className="text-[10px] text-slate-500">
                      Type: {purpose === 'site-visit' ? 'Indicative Data' : 'Draft Assessment'} ·
                      Standard: {stdCode}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => navigate('/daf-spec')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-rb-active text-white border border-rb-brand/20 hover:bg-rb-surface transition-all"
                    >
                      DAF Spec <ArrowRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => navigate('/influent')}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-800 text-slate-200 border border-slate-700 hover:bg-slate-700 transition-all"
                    >
                      Influent Records <ArrowRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={handleReset}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 border border-slate-800/50 hover:text-slate-300 transition-all"
                    >
                      Enter another sample
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
